import { message, Typography } from 'antd';
import { isArray } from 'lodash';
import Image from 'next/image';
import React, { memo, useCallback, useContext, useMemo } from 'react';

import MoreMenu from '@/components/common/MoreMenu';
import { MediaItem, TaskState } from '@/components/DrawerTaskPanel/useTaskList';
import { modelConfig } from '@/components/ModelControl/data';
import { RightContext } from '@/components/RightProvider/context';
import { PaymentModal, RenameAssetModal } from '@/components/UIComponents/CommonModal';
import { downloadTaskFile } from '@/services';
import { AIFunction, F_CLOUD_TASK_TYPE, ModelTypeEnum } from '@/utils/const';
import {
  AssetClickEnum,
  DownloadTier,
  EventNewID,
  StatusEnum,
  SubscribeReasonEnum,
} from '@/utils/eventlog/eventNewConfig';
import {
  getGTMAppName,
  getGTMFeatureName,
  pushGTMEvent,
} from '@/utils/eventlog/gtmDataLayer';
import { trackEvent } from '@/utils/eventlog/logAdapter';
import { downloadStreamFile } from '@/utils/fileUtil';
import { t } from '@/utils/helper/intl';
import { APP_VERSION } from '@/version';

import { CopyIcon, ProIcon } from './icon';
import styles from './index.module.scss';
import { MediaPreview } from './MediaPreview';
import { TaskCardProps } from './types';

export const getDownloadFeatureInfo = (mediaItems: MediaItem[], modelName?: string) => {
  let cloudTaskType: number | undefined;
  let featureName = AIFunction.Other;
  let currentModelName = modelName || null;
  let extraParams: any = {};
  const firstItem = mediaItems[0];

  if (firstItem?.rawData) {
    // 生成成功的资产，任务类型只有 customType, 生成中的和失败的都是 type，这里后端字段没统一
    const { extendInfo, customType, type } = firstItem.rawData as any;
    cloudTaskType = customType ? +customType : type;
    if (extendInfo?.taskExtraParam) {
      try {
        extraParams = JSON.parse(extendInfo.taskExtraParam);
        const { modelId, modelType } = extraParams;
        const extraModelName = modelConfig[modelType as ModelTypeEnum]?.find(
          (item) => item.modelValue === modelId,
        )?.modelName;
        if (!currentModelName && extraModelName) {
          currentModelName = extraModelName;
        }
      } catch (e) {
        console.error(e);
      }
    }
  }

  switch (cloudTaskType) {
    case F_CLOUD_TASK_TYPE.F_VIDEO_REMOVAL:
      featureName = AIFunction.Removal;
      break;
    case F_CLOUD_TASK_TYPE.F_AVATAR_VIDEO_2_0:
      featureName = AIFunction.Avatar2;
      break;
    case F_CLOUD_TASK_TYPE.F_PRODUCT_AVATAR:
      featureName = AIFunction.ProductAvatar;
      break;
    case F_CLOUD_TASK_TYPE.F_VIDEO_GENERATE:
      if (extraParams?.modelType === ModelTypeEnum.T2V) {
        featureName = AIFunction.Text2Video;
      } else if (extraParams?.modelType === ModelTypeEnum.I2V) {
        featureName = AIFunction.Image2Video;
      }
      break;
    case F_CLOUD_TASK_TYPE.F_BATCH_SUBTITLE_TRANS:
    case F_CLOUD_TASK_TYPE.F_BATCH_AUDIO_TRANS:
    case F_CLOUD_TASK_TYPE.F_BATCH_SUBTITLE_AUDIO_TRANS:
    case F_CLOUD_TASK_TYPE.F_MULTI_SUBTITLE_TRANS:
    case F_CLOUD_TASK_TYPE.F_MULTI_SUBTITLE_AUDIO_TRANS:
      featureName = AIFunction.VideoTranslate;
      break;
    case F_CLOUD_TASK_TYPE.F_FACE_SWAP:
      featureName = AIFunction.FaceSwap;
      break;
    case F_CLOUD_TASK_TYPE.F_HEAD_SWAP:
      featureName = AIFunction.HeadSwap;
      break;
    case F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE:
      if (extraParams?.modelType === ModelTypeEnum.T2I) {
        featureName = AIFunction.Text2Image;
      } else if (extraParams?.modelType === ModelTypeEnum.I2I) {
        featureName = AIFunction.Image2Image;
      }
      break;
    case F_CLOUD_TASK_TYPE.F_VOICE_CLONE:
      featureName = AIFunction.VoiceClone;
      break;
    case F_CLOUD_TASK_TYPE.F_TEXT_TO_SPEECH:
      featureName = AIFunction.Text2Speech;
      break;
    case F_CLOUD_TASK_TYPE.F_IMAGE_TRANSLATE:
      featureName = AIFunction.ImageTranslate;
      break;
    case F_CLOUD_TASK_TYPE.F_IMAGE_ENHANCE:
      featureName = AIFunction.ImageEnhance;
      break;
    case F_CLOUD_TASK_TYPE.F_VIDEO_EDIT:
      featureName = AIFunction.VideoEdit;
      break;
    case F_CLOUD_TASK_TYPE.F_VIRAL_REMAKE:
      featureName = AIFunction.ViralRemake;
      break;
    case F_CLOUD_TASK_TYPE.F_VIRAL_TEMPLATE:
      featureName = AIFunction.ViralTemplate;
      break;
  }

  return { featureName, modelName: currentModelName };
};

const TaskCard: React.FC<TaskCardProps> = memo(
  ({
    id,
    title,
    tags = [],
    mediaItems,
    cloudTaskType,
    createdAt,
    prompt,
    promptImage,
    promptLabel,
    onDelete,
    onDeleteBatch,
    onRename,
    renderHeader,
    renderMoreMenu,
    renderMediaLayout,
    modelName,
    'data-page': dataPage,
  }) => {
    const isNormal = mediaItems.every((item) => item.state === TaskState.SUCCESS);
    const isPending = mediaItems.some((item) => item.state === TaskState.PENDING);
    const hasError = mediaItems.some((item) => item.state === TaskState.FAILED);
    const rightContext = useContext(RightContext);
    const currentVip = rightContext.getIdentity();

    const images = (
      !isArray(promptImage) && promptImage ? [promptImage] : promptImage
    ) as string[];

    const handleDownload = useCallback(
      async ({
        downloadTier,
        type,
      }: {
        downloadTier: DownloadTier;
        type: AssetClickEnum;
      }) => {
        if (!isNormal) return;

        const { featureName, modelName: model_id } = getDownloadFeatureInfo(
          mediaItems,
          modelName,
        );

        trackEvent(EventNewID.ASSET_CLICK, {
          source: featureName,
          assetClickType: type,
        });

        // 逐文件上报 CREATIONS_DOWNLOAD
        mediaItems.forEach((item) => {
          trackEvent(EventNewID.CREATIONS_DOWNLOAD, {
            source: featureName,
            model_id,
            download_tier: downloadTier,
            task_id: (item.rawData as any)?.businessId || '',
          });
        });

        // GTM creation_download
        const gtmDownloadParams: Record<string, string> = {
          app_name: getGTMAppName(featureName),
          feature: getGTMFeatureName(featureName),
          download_tier:
            downloadTier === DownloadTier.PAID ? 'no_watermark' : 'with_watermark',
        };
        if (model_id) {
          gtmDownloadParams.model = model_id;
        }
        pushGTMEvent('creation_download', gtmDownloadParams as any);

        if (
          [F_CLOUD_TASK_TYPE.F_VOICE_CLONE, F_CLOUD_TASK_TYPE.F_TEXT_TO_SPEECH].includes(
            cloudTaskType,
          )
        ) {
          // 音频直接下载
          downloadStreamFile(
            mediaItems.map((item) => ({
              fileUrl: item.url,
              fileName: item.title,
            })),
          );
          // 逐文件上报 CREATIONS_DOWNLOAD_RESULT
          mediaItems.forEach((item) => {
            trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
              result: StatusEnum.SUCCESS,
              task_id: (item.rawData as any)?.businessId || '',
            });
          });
          return;
        }
        try {
          const ids = mediaItems.map((item) => item.id);
          const res = await downloadTaskFile(ids);
          if (res.code === 200) {
            let result = res.data || [];
            let array = result.map((item: any) => {
              return {
                fileUrl: item.fileUrl,
                fileName:
                  mediaItems.find((i) => i.rawData?.id === item.fileId)?.title ||
                  Date.now().toString(),
              };
            });
            downloadStreamFile(array);
            // 逐文件上报 CREATIONS_DOWNLOAD_RESULT
            mediaItems.forEach((item) => {
              trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
                result: StatusEnum.SUCCESS,
                task_id: (item.rawData as any)?.businessId || '',
              });
            });
          } else {
            mediaItems.forEach((item) => {
              trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
                result: StatusEnum.FAIL,
                task_id: (item.rawData as any)?.businessId || '',
              });
            });
            message.error(t('Download-Error'));
          }
        } catch {
          mediaItems.forEach((item) => {
            trackEvent(EventNewID.CREATIONS_DOWNLOAD_RESULT, {
              result: StatusEnum.FAIL,
              task_id: (item.rawData as any)?.businessId || '',
            });
          });
          message.error(t('Download-Error'));
        }
      },
      [mediaItems],
    );

    const onCopy = useCallback(() => {
      if (prompt) {
        navigator.clipboard.writeText(prompt);
        message.success(t('merror.operate.copied'));
      }
    }, [prompt]);

    // 单个菜单项构建器 - 供外部自由组合
    const menuItemBuilders = useMemo(() => {
      return {
        // 下载（带水印） - 免费用户
        downloadWithWatermark: {
          key: 'download-with',
          label: t('1fa392999e75851a4964321135e24fa8'),
          onClick: () => {
            handleDownload({
              downloadTier: DownloadTier.FREE,
              type: AssetClickEnum.download_with_watermark,
            });
          },
        },
        // 下载（无水印，需要升级） - 免费用户看到的升级选项
        downloadWithoutWatermark: {
          key: 'download-without',
          label: (
            <span className="flex justify-center">
              {t('6a804d250c194110364bbba5a76585de')}&nbsp;
              <ProIcon />
            </span>
          ),
          onClick: () => {
            const { featureName } = getDownloadFeatureInfo(mediaItems, modelName);
            trackEvent(EventNewID.ASSET_CLICK, {
              source: featureName,
              assetClickType: AssetClickEnum.download_no_watermark,
            });
            pushGTMEvent('view_pricing_plans', {
              trigger_type: 'usage_limit',
              app_name: getGTMAppName(featureName),
              app_version: APP_VERSION,
              feature: getGTMFeatureName(featureName),
              usage_limit: 'no_watermark_download',
            });
            PaymentModal.show({
              feature: featureName,
              reason: SubscribeReasonEnum.noWatermarkDownload,
            });
          },
        },
        rename: {
          key: 'rename',
          label: t('merror.operate.rename'),
          onClick: () => {
            const { featureName } = getDownloadFeatureInfo(mediaItems, modelName);
            trackEvent(EventNewID.ASSET_CLICK, {
              source: featureName,
              assetClickType: AssetClickEnum.rename,
            });
            const item = mediaItems[0];
            RenameAssetModal.show({
              id: item.id,
              title: item.title,
              onOk(newTitle: string) {
                // 通知父组件更新 UI，传递 itemId 和新标题
                onRename?.(item.id, newTitle);
              },
            });
          },
        },
        // 下载（无水印） - 付费用户
        download: {
          key: 'download',
          label: t('merror.operate.download'),
          onClick: () =>
            handleDownload({
              downloadTier: DownloadTier.PAID,
              type: AssetClickEnum.download,
            }),
        },
        // 删除
        delete: {
          key: 'delete',
          danger: true,
          label: t('Delete'),
          onClick: () => {
            const { featureName } = getDownloadFeatureInfo(mediaItems, modelName);
            trackEvent(EventNewID.ASSET_CLICK, {
              source: featureName,
              assetClickType: AssetClickEnum.delete,
            });
            onDeleteBatch?.();
          },
        },
      };
    }, [isPending, hasError, isNormal, currentVip, handleDownload]);

    // 准备 Header 渲染所需的数据（需要在 moreMenuItems 之前定义）
    const headerRenderProps = useMemo(
      () => ({
        id,
        title,
        tags,
        createdAt,
        cloudTaskType,
        mediaItems,
        isPending,
        hasError,
        isNormal,
        menuItemBuilders,
        modelName,
      }),
      [
        id,
        title,
        tags,
        createdAt,
        cloudTaskType,
        mediaItems,
        isPending,
        hasError,
        isNormal,
        menuItemBuilders,
        modelName,
      ],
    );

    // 默认菜单 - 使用 menuItemBuilders 组装
    const moreMenuItems = useMemo(() => {
      // 如果用户提供了 renderMoreMenu，使用用户自定义的
      if (renderMoreMenu) {
        return renderMoreMenu(menuItemBuilders, headerRenderProps);
      }

      return [];
    }, [renderMoreMenu, menuItemBuilders, headerRenderProps]);

    // 默认的 Header 内容渲染（只包含展示内容，不包含菜单）
    const defaultHeaderContent = useCallback(
      () => (
        <div className={styles.headerLeft}>
          <div className={styles.title}>{title}</div>
          <div className={styles.meta}>
            {tags?.length ? (
              <>
                <div className={styles.tags}>
                  {tags?.map((tag, index) => (
                    <span key={index} className={styles.tagItem}>
                      {tag}
                    </span>
                  ))}
                </div>
              </>
            ) : null}
            <div className={styles.divider} />
            <div className={styles.time}>{createdAt}</div>
          </div>
        </div>
      ),
      [title, tags, createdAt],
    );

    return (
      <div className={styles.taskCard} data-page={dataPage}>
        <div className={styles.header}>
          {renderHeader ? renderHeader(headerRenderProps) : defaultHeaderContent()}
          {moreMenuItems.length > 0 && (
            <div>
              <MoreMenu menu={moreMenuItems} />
            </div>
          )}
        </div>

        <div className={styles.content}>
          <MediaPreview
            cloudTaskType={cloudTaskType}
            items={mediaItems}
            renderLayout={renderMediaLayout}
            createdAt={createdAt}
            onDelete={(item) => {
              // 删除单个 mediaItem，传递 mediaItemId
              onDelete?.(item.id);
            }}
            onDeleteBatch={onDeleteBatch}
            onRename={(itemId, newTitle) => {
              // 重命名单个 mediaItem，传递 mediaItemId 和新标题
              onRename?.(itemId, newTitle);
            }}
          />

          {(prompt || images?.length) && (
            <div className={styles.promptSection}>
              <div className={styles.promptLabel}>
                <span onClick={onCopy}>
                  <CopyIcon />
                </span>
                {promptLabel || t('68a75660cc45dbee091e3fc745e7dc00')}:
              </div>
              {!!images?.length && (
                <div className="w-full flex flex-wrap gap-[5px]">
                  {images.map((item) => (
                    <div className={styles.promptImage} key={item}>
                      <Image
                        src={item}
                        alt="Prompt"
                        fill
                        unoptimized
                        className={styles.promptImageContent}
                      />
                    </div>
                  ))}
                </div>
              )}
              {prompt && (
                <Typography.Paragraph
                  ellipsis={{
                    rows: 3,
                    tooltip: {
                      title: prompt,
                      color: '#716fff',
                      classNames: {
                        root: '!max-w-[600px] ',
                        body: 'max-h-[200px] overflow-auto',
                      },
                    },
                  }}
                  className={styles.promptText}
                >
                  {prompt}
                </Typography.Paragraph>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

TaskCard.displayName = 'TaskCard';

export default TaskCard;
